import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError } from '@/api/errors';
import { apiClient } from '@/api/client';
import {
  mapCarePathwayTemplate,
  mapCarePathwayTemplates,
} from '@/features/patient-record/mappers/carePathway';
import type {
  CarePathwayTemplateState,
  CarePathwayTemplateViewModel,
} from '@/features/patient-record/types';

/**
 * Admin Care Pathways data hooks (PTV2-1174 Part B).
 *
 * Reads the full template catalogue (including DISABLED rows so the admin
 * page can show them) and exposes per-action mutation hooks that optimistic
 * update + rollback against the catalogue cache. Errors from the BE
 * (notably the 409 on delete-with-enrollments) bubble untouched so the
 * page can map them to the "disable instead" hint.
 */

const ADMIN_TEMPLATES_KEY = ['admin', 'care-pathway-templates'] as const;
const ADMIN_TEMPLATE_STALE_TIME = 30_000;

export const adminCarePathwayQueries = {
  templates: () =>
    queryOptions({
      queryKey: ADMIN_TEMPLATES_KEY,
      queryFn: ({ signal }) =>
        apiClient
          // `include_disabled: false` so the admin catalogue only shows the
          // active templates. Re-enabling a previously-disabled template
          // is a separate flow (a follow-up will add a "Show disabled"
          // toggle once the BE proves stable with that param).
          .listCarePathwayTemplates({ include_disabled: false }, { signal })
          .then(mapCarePathwayTemplates),
      staleTime: ADMIN_TEMPLATE_STALE_TIME,
    }),
};

export function useAdminCarePathwayTemplates(): UseQueryResult<CarePathwayTemplateViewModel[]> {
  return useQuery(adminCarePathwayQueries.templates());
}

export interface TemplateStepInput {
  /** Server id when editing an existing step; undefined for newly added rows. */
  readonly id?: string;
  readonly name: string;
  readonly description: string;
  readonly sortOrder: number;
}

export interface TemplateInput {
  readonly name: string;
  readonly description: string;
  readonly steps: readonly TemplateStepInput[];
}

interface MutationContext {
  readonly previous: CarePathwayTemplateViewModel[] | undefined;
}

export interface AdminCarePathwayTemplateMutations {
  readonly create: UseMutationResult<
    CarePathwayTemplateViewModel,
    Error,
    TemplateInput,
    MutationContext
  >;
  readonly update: UseMutationResult<
    CarePathwayTemplateViewModel,
    Error,
    { readonly id: string; readonly input: TemplateInput },
    MutationContext
  >;
  readonly setState: UseMutationResult<
    CarePathwayTemplateViewModel,
    Error,
    { readonly id: string; readonly state: CarePathwayTemplateState },
    MutationContext
  >;
  readonly remove: UseMutationResult<undefined, Error, string, MutationContext>;
}

function toDtoSteps(steps: readonly TemplateStepInput[]): readonly {
  readonly id?: string;
  readonly name: string;
  readonly description?: string;
  readonly sort_order: number;
}[] {
  return steps.map((s, idx) => ({
    ...(s.id !== undefined ? { id: s.id } : {}),
    name: s.name,
    description: s.description,
    sort_order: idx,
  }));
}

export function useAdminCarePathwayTemplateMutations(): AdminCarePathwayTemplateMutations {
  const queryClient = useQueryClient();

  const snapshot = (): MutationContext => ({
    previous: queryClient.getQueryData<CarePathwayTemplateViewModel[]>(
      ADMIN_TEMPLATES_KEY,
    ),
  });

  const rollback = (ctx: MutationContext | undefined): void => {
    if (ctx?.previous !== undefined) {
      queryClient.setQueryData(ADMIN_TEMPLATES_KEY, ctx.previous);
    }
  };

  const settle = (): void => {
    queryClient.invalidateQueries({ queryKey: ADMIN_TEMPLATES_KEY }).catch(() => undefined);
    // The patient-side assign dialog also reads templates (ACTIVE only) —
    // poke its cache so a freshly-disabled template disappears there too.
    queryClient
      .invalidateQueries({ queryKey: ['tenant', 'care-pathway-templates'] })
      .catch(() => undefined);
  };

  const create = useMutation<
    CarePathwayTemplateViewModel,
    Error,
    TemplateInput,
    MutationContext
  >({
    meta: { successMessage: 'success.toast.title.care_pathway_template_created' },
    mutationFn: async (input) => {
      const dto = await apiClient.createCarePathwayTemplate({
        name: input.name,
        description: input.description,
        steps: toDtoSteps(input.steps),
      });
      return mapCarePathwayTemplate(dto);
    },
    onMutate: () => snapshot(),
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  const update = useMutation<
    CarePathwayTemplateViewModel,
    Error,
    { readonly id: string; readonly input: TemplateInput },
    MutationContext
  >({
    meta: { successMessage: 'success.toast.title.care_pathway_template_updated' },
    mutationFn: async ({ id, input }) => {
      const dto = await apiClient.updateCarePathwayTemplate(id, {
        name: input.name,
        description: input.description,
        steps: toDtoSteps(input.steps),
      });
      return mapCarePathwayTemplate(dto);
    },
    onMutate: () => snapshot(),
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  const setState = useMutation<
    CarePathwayTemplateViewModel,
    Error,
    { readonly id: string; readonly state: CarePathwayTemplateState },
    MutationContext
  >({
    meta: { successMessage: 'success.toast.title.care_pathway_template_state_updated' },
    mutationFn: async ({ id, state }) => {
      const dto = await apiClient.setCarePathwayTemplateState(id, { state });
      return mapCarePathwayTemplate(dto);
    },
    onMutate: ({ id, state }) => {
      const ctx = snapshot();
      const list = ctx.previous;
      if (list !== undefined) {
        queryClient.setQueryData<CarePathwayTemplateViewModel[]>(
          ADMIN_TEMPLATES_KEY,
          list.map((t) => (t.id === id ? { ...t, state } : t)),
        );
      }
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  const remove = useMutation<undefined, Error, string, MutationContext>({
    meta: { successMessage: 'success.toast.title.care_pathway_template_deleted' },
    mutationFn: (id) => apiClient.deleteCarePathwayTemplate(id),
    onMutate: (id) => {
      const ctx = snapshot();
      const list = ctx.previous;
      if (list !== undefined) {
        queryClient.setQueryData<CarePathwayTemplateViewModel[]>(
          ADMIN_TEMPLATES_KEY,
          list.filter((t) => t.id !== id),
        );
      }
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      // BE returns 409 when enrollments exist — rolling back exposes the
      // row again so the page can show the "disable instead" suggestion.
      rollback(ctx);
    },
    onSettled: settle,
  });

  return { create, update, setState, remove };
}

/** Detects the BE's "delete blocked by enrollments" 409 so the UI can offer the disable shortcut. */
export function isDeleteBlockedByEnrollments(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409;
}
